import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../cdn-image.module.scss.js';
import { splitUrlQueryParams, joinUrlQueryParams } from '../../../utils/misc-utils.js';

var Video = function Video(_ref) {
  var _ref$alt = _ref.alt,
      alt = _ref$alt === void 0 ? '' : _ref$alt,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '' : _ref$height,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? Math.random().toString(36).replace(/[^a-z]+/g, '') : _ref$id,
      _ref$isPlaying = _ref.isPlaying,
      isPlaying = _ref$isPlaying === void 0 ? true : _ref$isPlaying,
      _ref$setIsLoading = _ref.setIsLoading,
      setIsLoading = _ref$setIsLoading === void 0 ? function () {} : _ref$setIsLoading,
      _ref$setIsPlaying = _ref.setIsPlaying,
      setIsPlaying = _ref$setIsPlaying === void 0 ? function () {} : _ref$setIsPlaying,
      _ref$src = _ref.src,
      src = _ref$src === void 0 ? '' : _ref$src,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? '' : _ref$width;
  var videoEl = useRef(null);
  var buttonText = "".concat(isPlaying ? 'Pause' : 'Play', " GIF");
  var buttonId = "gif-play-button-".concat(id);
  useEffect(function () {
    if (videoEl && videoEl.current && src) {
      // https://github.com/facebook/react/issues/10389#issuecomment-605689475
      // React has known bugs with muted video on certain mobile platforms
      // This will also log "Cannot flush updates when React is already rendering" warnings during testing
      // Thanks, I hate it.
      videoEl.current.setAttribute('muted', ''); // It's possible that the loadeddata event may have already fired at this point.
      // As a fallback, the following will first check if at least the current playback position has been loaded.

      if (videoEl.current.readyState >= 2) {
        setIsLoading(false);
      } else {
        videoEl.current.addEventListener('loadeddata', onVideoLoad);
      }
    }
  }, [src]);

  function onVideoLoad() {
    setIsLoading(false);
  }

  function changePlayState() {
    if (!videoEl || !videoEl.current) {
      return;
    }

    var paused = videoEl.current.paused;
    var stateChangeFn = paused ? 'play' : 'pause';

    if (typeof videoEl.current[stateChangeFn] === 'function') {
      videoEl.current[stateChangeFn](); // paused will be true if we are about to play (change play state to true)

      setIsPlaying(paused);
    }
  }

  if (!src) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.gifPlaceholder
    });
  }

  var _splitUrlQueryParams = splitUrlQueryParams(src),
      _splitUrlQueryParams2 = _slicedToArray(_splitUrlQueryParams, 2),
      cleanSrc = _splitUrlQueryParams2[0],
      paramStrings = _splitUrlQueryParams2[1];

  var videoParams = paramStrings.filter(function (p) {
    return !p.includes('output');
  }).concat(['output-format=mp4', 'output-quality=auto']);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: styles.gifPlayButton,
    onClick: changePlayState
  }, /*#__PURE__*/React.createElement("svg", {
    "aria-labelledby": buttonId,
    viewBox: "0 0 48 48",
    className: styles.gifPlayButton_icon,
    fill: "none",
    role: "img"
  }, /*#__PURE__*/React.createElement("title", {
    id: buttonId
  }, buttonText), /*#__PURE__*/React.createElement("circle", {
    className: styles.gifPlayButton_bg,
    cx: "24",
    cy: "24",
    r: "20",
    fill: "rgba(0, 0, 0, .25)"
  }), /*#__PURE__*/React.createElement("circle", {
    className: styles.gifPlayButton_circle,
    cx: "24",
    cy: "24",
    r: "18",
    stroke: "rgba(255, 255, 255, 1)",
    strokeWidth: "4"
  }), /*#__PURE__*/React.createElement("path", {
    className: styles.gifPlayButton_pauseCta,
    d: "M23.25,30V19.33H20.5V30ZM28,19.33H25.17V30h2.76Zm-.07,2.4,0,1.66"
  }), /*#__PURE__*/React.createElement("path", {
    className: styles.gifPlayButton_cta,
    d: "M17.416 30.192c2.016 0 3.584-.816 4.72-2.08v-4.24h-5.152v2.416h2.4v.832c-.384.32-1.184.64-1.968.64-1.744 0-2.976-1.344-2.976-3.088 0-1.76 1.232-3.088 2.976-3.088 1.04 0 1.856.656 2.256 1.36l2.304-1.2c-.736-1.328-2.176-2.592-4.56-2.592-3.184 0-5.776 2.128-5.776 5.52 0 3.392 2.592 5.52 5.776 5.52zM26.504 30V19.328h-2.752V30h2.752zm4.672 0v-4.208h4.96v-2.4h-4.96v-1.664h5.072v-2.4h-7.824V30h2.752z"
  }))), /*#__PURE__*/React.createElement("video", {
    "aria-label": "GIF: ".concat(alt),
    autoPlay: true,
    height: height,
    loop: true,
    muted: true,
    playsInline: true,
    ref: videoEl,
    width: width
  }, /*#__PURE__*/React.createElement("source", {
    type: "video/mp4",
    src: joinUrlQueryParams(cleanSrc, videoParams)
  })));
};

Video.propTypes = {
  alt: PropTypes.string,
  height: PropTypes.string,
  id: PropTypes.string,
  isPlaying: PropTypes.bool,
  setIsLoading: PropTypes.func,
  setIsPlaying: PropTypes.func,
  src: PropTypes.string,
  width: PropTypes.string
};

export { Video as default };
